// Teams view — grid of team cards. Each card shows the monogram tint,
// member count, and a chip indicating whether it's a custom team or
// projected from a department source. Selecting a card opens a sheet
// with the member roster, dashboard counts, and inline add/remove +
// lead toggles per member.
import { useState } from "react";
import { Plus, Users } from "lucide-react";
import { tpApi } from "../api";
import { useResource } from "../hooks";
import { Sheet } from "../components/Sheet";
import { Avatar } from "../components/Avatar";
import { KebabMenu } from "../components/KebabMenu";
import { CreateTeamSheet } from "../sheets/CreateTeamSheet";
import { PersonSelect } from "../sheets/createSlideOver/selects";
import type { TeamSummary } from "../../../../../../shared/team-planner/schemas";

export function TeamsView() {
  const teams = useResource(() => tpApi.listTeams(), []);
  const [openId, setOpenId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  return (
    <>
      <div className="tp-toolbar">
        <div className="tp-toolbar-left">
          <span style={{ color: "var(--bsp-fg-muted)", fontSize: 13 }}>
            {teams.data?.length ?? 0} team{teams.data?.length === 1 ? "" : "s"}
          </span>
        </div>
        <div className="tp-toolbar-right">
          <button
            type="button"
            className="bsp-btn bsp-btn-primary"
            onClick={() => setCreateOpen(true)}
          >
            <Plus size={14} /> New team
          </button>
        </div>
      </div>

      {teams.error ? (
        <p style={{ color: "var(--bsp-red-fg)" }}>Failed to load teams: {teams.error}</p>
      ) : null}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
          gap: 12,
        }}
      >
        {(teams.data ?? []).map((t) => (
          <button
            key={t.id}
            type="button"
            className="tp-card"
            onClick={() => setOpenId(t.id)}
            style={{ textAlign: "left" }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div
                aria-hidden
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 6,
                  background: "var(--bsp-blue-tint)",
                  color: "var(--bsp-blue)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  fontSize: 12,
                }}
              >
                {(t.monogram ?? t.name.slice(0, 2)).toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="tp-card-title">{t.name}</div>
                <div className="tp-card-meta">
                  <span>{t.memberCount} members</span>
                  <span>
                    {t.source === "department" ? (
                      <span className="bsp-chip bsp-chip-blue">Department</span>
                    ) : (
                      <span className="bsp-chip bsp-chip-grey">Custom</span>
                    )}
                  </span>
                </div>
              </div>
            </div>
            {t.description ? (
              <p style={{ fontSize: 12, color: "var(--bsp-fg-muted)", margin: 0 }}>
                {t.description}
              </p>
            ) : null}
          </button>
        ))}
        {(teams.data ?? []).length === 0 && !teams.loading ? (
          <div className="tp-empty">
            <Users size={20} aria-hidden style={{ margin: "0 auto 6px", display: "block" }} />
            <div>No teams yet.</div>
          </div>
        ) : null}
      </div>

      {openId ? (
        <TeamSheet
          id={openId}
          team={(teams.data ?? []).find((t) => t.id === openId) ?? null}
          onClose={() => setOpenId(null)}
          onChanged={() => teams.refresh()}
        />
      ) : null}
      {createOpen ? (
        <CreateTeamSheet
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            teams.refresh();
            setCreateOpen(false);
          }}
        />
      ) : null}
    </>
  );
}

function TeamSheet({
  id,
  team,
  onClose,
  onChanged,
}: {
  id: string;
  team: TeamSummary | null;
  onClose: () => void;
  onChanged: () => void;
}) {
  const members = useResource(() => tpApi.listTeamMembers(id), [id]);
  const dashboard = useResource(() => tpApi.teamDashboard(id), [id]);
  const [addOpen, setAddOpen] = useState(false);
  const [addPick, setAddPick] = useState<string | null>(null);
  const [addBusy, setAddBusy] = useState(false);

  async function addMember() {
    if (!addPick) return;
    setAddBusy(true);
    try {
      await tpApi.teamMembershipChange(id, { userId: addPick, action: "add" });
      setAddPick(null);
      setAddOpen(false);
      members.refresh();
      onChanged();
    } finally {
      setAddBusy(false);
    }
  }

  async function memberAction(userId: string, action: "remove" | "set_lead" | "unset_lead") {
    await tpApi.teamMembershipChange(id, { userId, action });
    members.refresh();
    onChanged();
  }

  return (
    <Sheet open onClose={onClose} title={team?.name ?? "Team"}>
      {!team ? null : (
        <>
          {team.description ? (
            <p style={{ fontSize: 13, lineHeight: 1.45, margin: 0 }}>{team.description}</p>
          ) : null}

          <section>
            <div className="bsp-label">Counts</div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: 8,
              }}
            >
              <CountTile label="Rocks on track" value={dashboard.data?.rocksOnTrack ?? 0} tone="green" />
              <CountTile label="Rocks off track" value={dashboard.data?.rocksOffTrack ?? 0} tone="red" />
              <CountTile label="To-dos open" value={dashboard.data?.todosOpen ?? 0} tone="blue" />
              <CountTile label="Opportunities open" value={dashboard.data?.opportunitiesOpen ?? 0} tone="orange" />
            </div>
          </section>

          <section>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: 8,
              }}
            >
              <div className="bsp-label" style={{ flex: 1, marginBottom: 0 }}>
                Members · {members.data?.length ?? 0}
              </div>
              <button
                type="button"
                className="bsp-btn bsp-btn-secondary bsp-btn-sm"
                onClick={() => setAddOpen((v) => !v)}
              >
                <Plus size={12} /> Add member
              </button>
            </div>

            {addOpen ? (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto",
                  gap: 8,
                  marginBottom: 10,
                }}
              >
                {/* Tenant-wide PersonSelect lives in createSlideOver/selects;
                    reuses the same /users search the create forms use. Once
                    section C ships the Graph-backed search, this picker
                    automatically surfaces every tenant user. */}
                <PersonSelect value={addPick} onChange={setAddPick} />
                <button
                  type="button"
                  className="bsp-btn bsp-btn-primary bsp-btn-sm"
                  disabled={!addPick || addBusy}
                  onClick={addMember}
                >
                  {addBusy ? "Adding…" : "Add"}
                </button>
              </div>
            ) : null}

            <ul
              style={{
                listStyle: "none",
                padding: 0,
                margin: 0,
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              {(members.data ?? []).map((m) => (
                <li
                  key={m.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "8px 10px",
                    border: "1px solid var(--bsp-border)",
                    borderRadius: 4,
                    background: "var(--bsp-surface)",
                  }}
                >
                  <Avatar owner={{ displayName: m.displayName, email: m.email }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>
                      {m.displayName ?? "Unknown"}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--bsp-fg-muted)" }}>
                      {m.email}
                    </div>
                  </div>
                  {m.role === "lead" ? (
                    <span className="bsp-chip bsp-chip-blue">Lead</span>
                  ) : null}
                  <KebabMenu
                    actions={
                      m.role === "lead"
                        ? [
                            { label: "Demote to member", onClick: () => memberAction(m.userId, "unset_lead") },
                            { label: "_divider", divider: true, onClick: () => undefined },
                            { label: "Remove from team", danger: true, onClick: () => memberAction(m.userId, "remove") },
                          ]
                        : [
                            { label: "Promote to lead", onClick: () => memberAction(m.userId, "set_lead") },
                            { label: "_divider", divider: true, onClick: () => undefined },
                            { label: "Remove from team", danger: true, onClick: () => memberAction(m.userId, "remove") },
                          ]
                    }
                  />
                </li>
              ))}
              {(members.data ?? []).length === 0 && !members.loading ? (
                <li className="tp-empty">No members yet — click Add member.</li>
              ) : null}
            </ul>
          </section>
        </>
      )}
    </Sheet>
  );
}

function CountTile({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: "blue" | "green" | "red" | "orange";
}) {
  const color = {
    blue: "var(--bsp-blue)",
    green: "var(--bsp-green)",
    red: "var(--bsp-red)",
    orange: "var(--bsp-orange)",
  }[tone];
  return (
    <div className="tp-stat-tile" style={{ padding: "10px 12px" }}>
      <div className="tp-stat-label" style={{ fontSize: 9 }}>
        {label}
      </div>
      <div
        className="tp-stat-value"
        style={{ fontSize: 22, color: value === 0 ? "var(--bsp-fg-muted)" : color }}
      >
        {value}
      </div>
    </div>
  );
}
